import * as React from 'react';
import * as _ from 'lodash';
import PopupWrapper from '../../../../components/Popup/PopupWrapper';
import PopupHeader from '../../../../components/Popup/PopupHeader';
import PopupBody from '../../../../components/Popup/PopupBody';
import withClickOutsideOnOverlay from '../../../../../hoc/withClickOutsideOnOverlay';
import { CodeMirrorType } from '../../../../../models/CodeMirrorType';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import getCodeMirrorInstance from '../../../../../services/mini/getCodeMirrorInstance';
import TransformRowPopupColumn from '../TransformRowPopupColumn/TransformRowPopupColumn';
import { ITransformHeader, ITransformRow, TransformFormatType } from '../../../../../models/ITransform';
import isLocalStorageAvailable from '../../../../../services/mini/isLocalStorageAvailable';
import { disableTwoFingersSwipe, enableTwoFingersSwipe } from '../../../../../services/mini/twoFingersGestureHelper';
import { Environment } from '../../../../../const/environment';
import { transformFormatsEditorModes } from '../transformFormats';
import { observer } from 'mobx-react';
import { transformsStore } from '../../../../../mobx/transforms.store';
import { getTransformRowCells } from '../getTransformRowCells';
import PopupButtons from '../../../../components/Popup/PopupButtons';
import PopupButton from '../../../../components/Popup/PopupButton';
import { useClosePopup } from '../../../../../hooks/useClosePopup';
import TransformFnInfoPopup from '../TransformFnInfoPopup/TransformFnInfoPopup';
import store from '../../../../../store';
import ScriptsProgressTracker from '../ScriptsProgressTracker/ScriptsProgressTracker';
import { TransformViewerMode } from '../../../../../models/TransformViewerMode';


type PanelsState = {
    input: { collapsed: false, colMode: TransformViewerMode },
    query: { collapsed: false, colMode: TransformViewerMode },
    prompt?: { collapsed: false, colMode: TransformViewerMode },
    result: { collapsed: false, colMode: TransformViewerMode },
};


type TransformRowPopupProps = {
    row: ITransformRow
    openedBy?: string
    transformName: string
    environment: Environment
    placeholder?: string
    previewMode: boolean
    editable: boolean
    header: ITransformHeader,
    onClose: () => void;
    projectId: string;
    index: number;
}

const LS_PANELS_KEY = `transform-panels-key`;
const LS_PANELS_PREVIEW_KEY = `transform-panels-preview-mode-key`;

function getTransformRowPopupLSKey(previewMode) {
    return `${previewMode ? LS_PANELS_PREVIEW_KEY : LS_PANELS_KEY}`;
}

function savePanelsState(previewMode: boolean, panels) {
    console.info('panels', panels);
    if (isLocalStorageAvailable()) {
        localStorage.setItem(getTransformRowPopupLSKey(previewMode), JSON.stringify(panels));
    }
}

function getPanelsState(previewMode, openedBy, header): PanelsState {
    const defaultState: PanelsState = previewMode ? {
        input: { collapsed: false, colMode: 'editor' },
        query: { collapsed: false, colMode: 'editor' },
        result: { collapsed: false, colMode: 'editor' },
    } : {
        input: { collapsed: false, colMode: 'editor' },
        query: { collapsed: false, colMode: 'editor' },
        prompt: { collapsed: false, colMode: 'editor' },
        result: { collapsed: false, colMode: 'editor' },
    };

    if (isLocalStorageAvailable()) {
        try {
            const savedState = JSON.parse(localStorage.getItem(getTransformRowPopupLSKey(previewMode)));
            const columns = ['input', 'prompt', 'query', 'result'];

            for (let i = 0; i < columns.length; i++) {
                const curCol = columns[i];
                if (savedState[curCol] && !transformFormatsEditorModes[header[curCol]?.format]?.[savedState[curCol]?.colMode]) {
                    savedState[curCol].colMode = 'editor';
                }
            }

            if (savedState && savedState[openedBy]?.collapsed === true) {
                savedState[openedBy].collapsed = false;
            }

            return savedState || defaultState;
        } catch (error) {
        }
    }
    return defaultState;
}


function TransformRowPopup({
    row,
    index,
    header,
    environment,
    previewMode,
    openedBy,
    editable,
    transformName,
    projectId,
    onClose,
}: TransformRowPopupProps) {

    const [collapseOtherPanelsByClickOnSplitMode, setCollapseOtherPanelsByClickOnSplitMode] = useState(false);
    const [headerColumns, setHeaderColumns] = useState([header.input?.format || 'text', header.query?.format || 'text', header.prompt?.format || 'text', header.result?.format || 'text']);
    const [panels, setPanels] = useState(getPanelsState(previewMode, openedBy, header));
    const ref = useRef<CodeMirrorType>();

    const onUpdateRowCb = useCallback((newVal, name) => {
        transformsStore.updateRow(name, newVal, row.id);
    }, [row.id]);

    const onToggleColumnCb = useCallback((collapsed: boolean, name: string) => {
        setPanels(prevVal => {
            const newPanelsState = {
                ...prevVal,
                [name]: {
                    ...prevVal[name],
                    collapsed
                }
            };
            savePanelsState(previewMode, newPanelsState);
            return newPanelsState;
        });
    }, [previewMode, projectId]);

    const onFormatChangeCb = useCallback((name: string, value: TransformFormatType) => {
        transformsStore.updateFormat(name, value);
        const columns = ['input', 'query', 'prompt', 'result'];
        const ind = columns.indexOf(name);
        if (ind > -1) {
            setHeaderColumns(prevValue => {
                const newFormats = [...prevValue];
                newFormats[ind] = value;
                return newFormats;
            });
        }

        setPanels(prevVal => {
            let newPanels = { ...prevVal };

            if (!transformFormatsEditorModes[value][prevVal[name].colMode]) {
                newPanels = {
                    ...newPanels,
                    [name]: {
                        colMode: 'editor',
                        collapsed: newPanels[name].collapsed
                    }
                };
            };

            savePanelsState(previewMode, newPanels);
            return newPanels;
        });
    }, []);

    const onSplitModeClickCb = useCallback((name: string, modeName: string) => {
        if (modeName === 'both') {
            setPanels(prevVal => {
                let newPanels = { ...prevVal };

                if (collapseOtherPanelsByClickOnSplitMode) {
                    for (const key in newPanels) {
                        newPanels[key].collapsed = collapseOtherPanelsByClickOnSplitMode;
                    }
                }
                newPanels = {
                    ...newPanels, [name]: {
                        colMode: modeName,
                        collapsed: false
                    }
                };
                savePanelsState(previewMode, newPanels);
                return newPanels;
            });
        } else {
            setPanels(prevVal => {
                const newPanels = {
                    ...prevVal,
                    [name]: {
                        colMode: modeName,
                        collapsed: false
                    }
                };
                savePanelsState(previewMode, newPanels);
                return newPanels;
            });
        }

    }, [collapseOtherPanelsByClickOnSplitMode, previewMode, projectId]);

    const refEl = useRef<HTMLDivElement>();

    const onCancelCb = useCallback(() => {
        onClose();
    }, [onClose]);

    useEffect(() => {
        if (editable) {
            getCodeMirrorInstance(ref.current)?.focus();
        }
    }, [editable]);

    useClosePopup(onCancelCb, disableTwoFingersSwipe, enableTwoFingersSwipe)

    const collapsedColumnCount = Object.values(panels).filter(item => item.collapsed).length;
    const maxCollapsedColCount = previewMode ? 2 : 3;
    const canBeCollapsed = collapsedColumnCount < maxCollapsedColCount;

    const getFr = (field) => {
        if (field.collapsed) return '22px';

        return `${field.colMode === 'both' ? 2 : 1}fr`;
    };

    const cols = !previewMode ?
        `${getFr(panels.input)} ${getFr(panels.query)} ${getFr(panels.prompt)} ${getFr(panels.result)}` :
        `${getFr(panels.input)} ${getFr(panels.query)} ${getFr(panels.result)}`;

    const cells = useMemo(() => {
        return getTransformRowCells(previewMode);
    }, [previewMode]);

    return (
        <PopupWrapper className={`transform-row-popup ${!editable ? 'read-only-mode' : ''}`}>
            <PopupHeader className="transform-row-popup__small-header"
                title={``}
                onCancel={onCancelCb}>
                {/* <ScriptsProgressTracker store={store} /> */}
            </PopupHeader>

            <PopupBody className="transform-row-popup__body">
                <div className={`transform-row-popup__cols`} style={{ gridTemplateColumns: cols }} ref={refEl}>
                    {cells.map((cell) => {
                        return <TransformRowPopupColumn
                            key={cell.name}
                            initValue={row[cell.name]}
                            title={cell.title}
                            name={cell.name}
                            colMode={panels[cell.name].colMode}
                            format={headerColumns[cell.headerColumnIndex]}
                            editable={editable}
                            hideFormatSelector={cell.name === 'prompt'}
                            onUpdate={onUpdateRowCb}
                            collapsed={panels[cell.name].collapsed}
                            canBeCollapsed={canBeCollapsed}
                            onToggleColumn={onToggleColumnCb}
                            onSplitModeClick={onSplitModeClickCb}
                            onFormatChange={onFormatChangeCb}
                            row={row}
                            transformName={transformName}
                            environment={environment}
                            index={index}
                        />
                    })}
                </div>
            </PopupBody>
            <PopupButtons className="transform-row-popup__btns">
                <PopupButton
                    className={`transform-view__cell-popup-ok-btn `}
                    label={"Back to transform"}
                    action={() => {
                        onCancelCb();
                    }} />
            </PopupButtons>
            <TransformFnInfoPopup store={store} />
        </PopupWrapper>
    );
}

export default observer(TransformRowPopup);